import { useState, useEffect } from 'react';
import { getSupabaseClient } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { PasswordInput } from '@ui';
import { Footer } from '@ui/components/Footer';
import { SeekerHeader } from '@ui/components/SeekerHeader';

export function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validatingCode, setValidatingCode] = useState(true);
  const [codeError, setCodeError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Parse hash fragment and set session
    const handleSession = async () => {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      const type = hashParams.get('type');

      if (!accessToken || !refreshToken || type !== 'recovery') {
        setCodeError('No reset token found. Please request a new password reset link.');
        setValidatingCode(false);
        return;
      }

      try {
        const supabase = getSupabaseClient();
        const { error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });

        if (error) {
          setCodeError('Invalid or expired reset link. Please request a new one.');
        }
      } catch (err) {
        console.error('Session setup error:', err);
        setCodeError('Something went wrong. Please try again.');
      } finally {
        setValidatingCode(false);
      }
    };

    handleSession();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const supabase = getSupabaseClient();
      const { error } = await supabase.auth.updateUser({ password });

      if (error) {
        setError(error.message);
        setLoading(false);
      } else {
        setSuccess(true);
        setLoading(false);
        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    } catch (err) {
      console.error('Password update error:', err);
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const inputClasses =
    'w-full border border-[#304031]/30 rounded-lg px-3 py-2 text-sm bg-[#FFFCEF] text-[#304031] placeholder:text-[#304031]/40 focus:outline-none focus:ring-1 focus:ring-[#304031] focus:border-[#304031] transition-colors';

  if (validatingCode) {
    return (
      <div className="flex min-h-screen w-full flex-col lg:overflow-auto overflow-hidden">
        <div className="relative flex flex-1 w-full flex-col bg-[#FFFCEF]">
          <header className="flex lg:hidden h-16 shrink-0 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
            <div className="w-10" aria-hidden />
            <div className="flex flex-1 justify-center">
              <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
            </div>
            <div className="w-10" aria-hidden />
          </header>
          <div className="hidden lg:block">
            <SeekerHeader />
          </div>

          <div className="flex flex-1 flex-col items-center lg:px-[120px] lg:py-16">
            <main className="flex flex-1 lg:flex-initial min-h-0 flex-col items-center justify-center px-6 lg:px-0 w-full max-w-[480px]">
              <div className="text-center">
                <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-[#304031]"></div>
                <p className="text-[#304031]/70">Validating reset link...</p>
              </div>
            </main>
          </div>
        </div>

        <div className="hidden lg:block">
          <Footer />
        </div>
      </div>
    );
  }

  if (codeError) {
    return (
      <div className="flex min-h-screen w-full flex-col lg:overflow-auto overflow-hidden">
        <div className="relative flex flex-1 w-full flex-col bg-[#FFFCEF]">
          <header className="flex lg:hidden h-16 shrink-0 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
            <button
              type="button"
              onClick={() => navigate('/auth/forgot')}
              className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-[#FEF6D6]/30 bg-transparent text-xl text-[#FEF6D6] transition hover:bg-[#FEF6D6]/10"
              aria-label="Go back"
            >
              {'\u2190'}
            </button>
            <div className="flex flex-1 justify-center">
              <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
            </div>
            <div className="w-10" aria-hidden />
          </header>
          <div className="hidden lg:block">
            <SeekerHeader />
          </div>

          <div className="flex flex-1 flex-col items-center lg:px-[120px] lg:py-16">
            <main className="flex flex-1 lg:flex-initial min-h-0 flex-col items-center justify-center px-6 lg:px-0 w-full max-w-[480px]">
              <div className="w-full rounded-2xl border border-[#304031]/15 bg-[#FFFCEF] p-8 text-center">
                <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                  <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>

                <h1 className="mb-2 font-recoleta text-[28px] leading-[1.1] text-[#304031]">
                  Link Expired
                </h1>
                <p className="mb-6 text-sm leading-relaxed text-[#304031]/70">
                  {codeError}
                </p>

                <button
                  onClick={() => navigate('/auth/forgot')}
                  className="w-full rounded-full bg-[#304031] px-6 py-4 text-base font-semibold text-[#FEF6D6] transition hover:bg-[#304031]/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#304031]/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#FFFCEF]"
                >
                  Request New Reset Link
                </button>
              </div>
            </main>
          </div>
        </div>

        <div className="hidden lg:block">
          <Footer />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-full flex-col lg:overflow-auto overflow-hidden">
      <div className="relative flex flex-1 w-full flex-col bg-[#FFFCEF]">
        <header className="flex lg:hidden h-16 shrink-0 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
          <div className="w-10" aria-hidden />
          <div className="flex flex-1 justify-center">
            <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
          </div>
          <div className="w-10" aria-hidden />
        </header>
        <div className="hidden lg:block">
          <SeekerHeader />
        </div>

        <div className="flex flex-1 flex-col items-center lg:px-[120px] lg:py-16">
          <main className="flex flex-1 lg:flex-initial min-h-0 flex-col items-center justify-center px-6 lg:px-0 w-full max-w-[480px]">
            <div className="w-full rounded-2xl border border-[#304031]/15 bg-[#FFFCEF] p-8 text-center">
              <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[#304031]/10">
                <svg className="h-8 w-8 text-[#304031]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>

              <h1 className="mb-2 font-recoleta text-[28px] leading-[1.1] text-[#304031]">
                Set New Password
              </h1>
              <p className="mb-6 text-sm leading-relaxed text-[#304031]/70">
                Enter your new password below.
              </p>

              {success ? (
                <div>
                  <div className="mb-6 rounded-2xl border border-[#4CAF50]/30 bg-[#E8F5E9] px-4 py-4 text-sm text-green-800">
                    <p className="font-medium">Password updated successfully!</p>
                    <p className="mt-1 text-green-700">Redirecting you to login...</p>
                  </div>
                </div>
              ) : (
                <form className="space-y-4 text-left" onSubmit={handleSubmit}>
                  {error && (
                    <div className="rounded-2xl border border-[#B24E4E]/30 bg-[#FCEDEA] px-4 py-3 text-sm font-medium text-[#B24E4E]">
                      {error}
                    </div>
                  )}

                  <div>
                    <label className="mb-1 block text-sm font-medium text-[#304031]">
                      New Password
                    </label>
                    <PasswordInput
                      placeholder="Enter your new password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={inputClasses}
                      required
                      minLength={8}
                    />
                    <p className="mt-1 text-xs text-[#304031]/60">
                      Must be at least 8 characters
                    </p>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-[#304031]">
                      Confirm Password
                    </label>
                    <PasswordInput
                      placeholder="Confirm your new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={inputClasses}
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className={`inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-4 text-base font-semibold transition ${
                      loading
                        ? 'cursor-not-allowed bg-[#304031]/60 text-[#FEF6D6]/80'
                        : 'bg-[#304031] text-[#FEF6D6] hover:bg-[#304031]/90'
                    } focus:outline-none focus-visible:ring-2 focus-visible:ring-[#304031]/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#FFFCEF]`}
                  >
                    {loading ? (
                      <>
                        <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#FEF6D6] border-t-transparent"></div>
                        Updating...
                      </>
                    ) : (
                      'Update Password'
                    )}
                  </button>
                </form>
              )}
            </div>
          </main>
        </div>
      </div>

      <div className="hidden lg:block">
        <Footer />
      </div>
    </div>
  );
}
